import { Controller } from 'react-hook-form';

import { ChangeIcon } from '@/shared/assets';
import { ExchangeChart } from '@/features/exchange';
import { EXCHANGE_BLOCK_ID, getValueWithCurrency } from '@/shared/lib';
import { Button, Form, Heading, ScreenCard, Spinner, Typography } from '@/shared/ui';

import { CurrencyWithAmount } from './currency-with-amount';
import { ExchangeReview } from './exchange-review.tsx';
import { twMerge } from 'tailwind-merge';
import { useExchangeCalculator } from '../model/exchange';
import { ICurrencyAccount } from '@/entities/currency-account';

interface ExchangeCalculatorProps {
  isWidget?: boolean;
}

export const ExchangeCalculator = ({ isWidget }: ExchangeCalculatorProps) => {
  const {
    form,
    exchangeRates,
    rateLabel,
    isRecalculating,
    recalculationError,
    fee,
    fromCurrency,
    toCurrency,
    fromAmount,
    toAmount,
    recalculateSide,
    fromCurrencyCode,
    toCurrencyCode,
    markRecalculate,
    handleSwap,
  } = useExchangeCalculator();

  const { control, setValue } = form;

  return (
    <ScreenCard
      className={twMerge("mx-auto flex flex-col gap-y-5 p-1 pt-5 md:flex-row md:gap-y-0 md:p-6", isWidget && "md:p-1")}
      id={isWidget ? "ex-widget" : EXCHANGE_BLOCK_ID}
    >
      {!isWidget && <div className="flex flex-col gap-2 md:w-1/2">
        <div className="flex w-full flex-col gap-[6px] px-4 md:pl-0 md:pr-4">
          <Typography variant="14-r" color="text-base-100">
            Exchange calculator
          </Typography>
          <Heading className="text-left font-black" variant="s" tag="span">
            1 {fromCurrency?.code || fromCurrencyCode} = {rateLabel}{' '}
            {toCurrency?.code || toCurrencyCode}
          </Heading>
        </div>
        <ExchangeChart exchangeRates={exchangeRates} />
      </div>}
      <Form {...form}>
        <form
          className={twMerge(
            'flex w-full flex-col gap-1 md:w-1/2 md:pl-4',
            isWidget && 'md:w-full md:pl-0',
          )}
        >
          <div className="relative rounded-6xl bg-base-300">
            <Controller
              name="fromAmount"
              control={control}
              render={({ field }) => (
                <CurrencyWithAmount
                  isDisabled={isRecalculating}
                  isAmountDisabled={false}
                  {...field}
                  excludedCodes={[toCurrency?.code]}
                  currency={fromCurrency}
                  onCurrencyChange={(currency: ICurrencyAccount) => {
                    setValue('fromCurrency', currency);
                    markRecalculate(recalculateSide);
                  }}
                  onAmountChange={(value) => {
                    field.onChange(value);
                    markRecalculate('from');
                  }}
                />
              )}
            />
            <div className="h-[1px] w-full bg-surface-03" />
            <Controller
              name="toAmount"
              control={control}
              render={({ field }) => (
                <CurrencyWithAmount
                  {...field}
                  isDisabled={isRecalculating}
                  isAmountDisabled={false}
                  excludedCodes={[fromCurrency?.code]}
                  currency={toCurrency}
                  onCurrencyChange={(currency: ICurrencyAccount) => {
                    setValue('toCurrency', currency);
                    markRecalculate(recalculateSide);
                  }}
                  onAmountChange={(value) => {
                    field.onChange(value);
                    markRecalculate('to');
                  }}
                />
              )}
            />
            <Button
              onClick={handleSwap}
              type="button"
              variant="base-100"
              disabled={isRecalculating}
              className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 transform rounded-full p-0"
            >
              <ChangeIcon className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-col gap-5 rounded-3xl bg-base-300 p-5">
            <div className="flex items-center justify-between">
              <Typography color="text-base-100" variant="16-r">
                Fees (included)
              </Typography>
              <Typography variant="16-r">
                {isRecalculating ? <Spinner size="sm" /> : getValueWithCurrency(fee, fromCurrency.symbol)}
              </Typography>
            </div>
            {!!recalculationError && (
              <Typography variant="14-r" color="text-negative-500">
                {recalculationError}
              </Typography>
            )}
            <div className="flex items-center justify-between">
              <Typography color="text-base-100" variant="16-r">
                Estimated arrival
              </Typography>
              <Typography variant="16-r">1 min</Typography>
            </div>
          </div>
          <ExchangeReview isWidget={isWidget} />
        </form>
      </Form>
    </ScreenCard>
  );
};
